// Trading behavior detection, grouped by the emotion behind it: tilt (revenge, sizing up after a
// loss, trading on after a losing streak, past the daily loss limit), greed (giving back the
// day's profit, sizing up after wins), fear (cutting winners short), FOMO (trades outside your
// trade ideas) and overtrading or boredom (too many trades, unusual hours). Every flag comes from
// the trades' own times, sizes and results. What the trader reported (End-of-day emotions and the
// Live session reset buttons) is kept apart, so the two can be compared. Shown by BehaviorInsights.js.
import { dayKey, formatDuration, formatMoney, holdMs, netPnl, tradingDayKey } from './pnl';
import { pointValue } from './tools/contracts';
import { translateNow as tn } from './langState';

// Change the texts in PATTERNS too if these change.
export const REVENGE_MINUTES = 10;
export const STREAK = 3;
// Trades placed in several accounts within this many seconds (copy trading) count as one decision.
const SAME_DECISION_MS = 5000;
// Usual hours need this many trades with a time before any trade can be "off-hours".
const MIN_FOR_HOURS = 20;
const USUAL_SHARE = 0.85;
// Cutting winners short needs this many winners and losers with exit times.
const MIN_FOR_HOLDS = 8;

export const PATTERNS = [
  { kind: 'revenge', group: 'tilt', name: 'Revenge trades',
    what: 'A trade entered within 10 minutes after a losing trade closed, the same day.',
    rule: 'After a loss, I wait 10 minutes before my next trade.' },
  { kind: 'sizeUp', group: 'tilt', name: 'Sizing up after a loss',
    what: 'A bigger position right after a loss: at least 1.5 times the last trade and more than you usually trade.',
    rule: 'After a loss, my next trade is the same size or smaller.' },
  { kind: 'streak', group: 'tilt', name: 'Trading on after 3 losses',
    what: 'Any trade taken after 3 or more losses in a row the same day.',
    rule: 'After 3 losses in a row, I stop trading for the day.' },
  { kind: 'pastLimit', group: 'tilt', name: 'Past your daily loss limit',
    what: 'Trades taken after the account’s loss that day had already reached its daily loss limit.',
    rule: 'When I hit my daily loss limit, I’m done for the day.' },
  { kind: 'giveBack', group: 'greed', name: 'Giving back the day’s profit',
    what: 'Losing trades after your best moment of the day, on days you gave back at least half of it.',
    rule: 'When I give back half of my best P&L of the day, I stop.' },
  { kind: 'winSizeUp', group: 'greed', name: 'Sizing up after wins',
    what: 'A bigger position after 2 or more wins in a row: at least 1.5 times the last trade and more than you usually trade.',
    rule: 'After a winning streak, I keep my normal size.' },
  { kind: 'cutWinners', group: 'fear', name: 'Cutting winners short', costless: true,
    what: 'Winners closed in less than half the time you usually hold a losing trade. Small quick wins with long losers is the classic fear pattern.',
    rule: 'I hold winners to my planned target unless my exit rule says go.' },
  { kind: 'unplanned', group: 'fomo', name: 'Trades outside your plan',
    what: 'Trades on days you wrote trade ideas that weren’t one of them. Chasing a move you didn’t plan is the most common form of FOMO.',
    rule: 'I only take trades from my trade ideas.' },
  { kind: 'overtrading', group: 'boredom', name: 'Overtrading',
    what: 'Trades past twice your usual number of trades in a day (and past 4).',
    rule: 'I take no more than {n} trades a day.' },
  { kind: 'offHours', group: 'boredom', name: 'Outside your usual hours',
    what: 'Trades in a half hour (New York time) where you rarely trade. Your usual hours come from where 85% of your trades happen.',
    rule: 'I only trade during my planned hours.' },
];

// `feelings` are the ids saved by the End-of-day emotions (Emotions.js) and Live session reset
// buttons (Reset.js). `signal` says how clearly trades alone can show the behavior.
export const GROUPS = [
  { id: 'tilt', name: 'Tilt', face: '🌀', signal: 'strong', feelings: ['Tilt', 'Revenge trading'] },
  { id: 'greed', name: 'Greed', face: '🤑', signal: 'good', feelings: ['Greed', 'Overconfidence'] },
  { id: 'fear', name: 'Fear', face: '😨', signal: 'good', feelings: ['Fear / hesitation'] },
  { id: 'fomo', name: 'FOMO', face: '😱', signal: 'weak', feelings: ['FOMO', 'Impatience'] },
  { id: 'boredom', name: 'Overtrading & boredom', face: '🥱', signal: 'good', feelings: ['Boredom / overtrading'] },
];

const ms = (d) => new Date(d).getTime();
const exitMs = (t) => ms(t.exitDate || t.date);
const median = (list) => {
  if (!list.length) return 0;
  const s = [...list].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
// Dollars per point times contracts (futures), or shares times price (stocks), so micros and
// minis compare fairly.
const exposure = (t) => {
  const pv = pointValue(t.symbol);
  return Math.abs(t.size || 1) * (pv == null || pv === 1 ? Math.max(1, Math.abs(t.entryPrice || 1)) : pv);
};
const NY = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour12: false, hour: '2-digit', minute: '2-digit' });
// The half hour of the day a trade was entered, New York time: 0 = midnight–12:30, 19 = 9:30–10:00.
const nySlot = (t) => {
  const parts = NY.formatToParts(new Date(t.date));
  const get = (k) => Number(parts.find((p) => p.type === k).value);
  return (get('hour') % 24) * 2 + (get('minute') >= 30 ? 1 : 0);
};
export const slotLabel = (slot) => {
  const h = Math.floor(slot / 2) % 24;
  const m = slot % 2 ? '30' : '00';
  return `${h % 12 || 12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
};
const clock = (t) => new Date(t).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });

// Groups trades into decisions: the same symbol and side entered within a few seconds in
// different accounts is one decision (copy trading), with the combined result.
function decisionsOf(trades) {
  const timed = trades.filter((t) => !t.timeUnknown).sort((a, b) => ms(a.date) - ms(b.date));
  const out = [];
  // Trades the trader linked as one trade idea (scaling in, partial exits) are one decision.
  const byLink = new Map();
  for (const t of timed) {
    const linked = t.linkId && byLink.get(t.linkId);
    if (linked) {
      linked.trades.push(t);
      if (linked.accountIds.indexOf(String(t.accountId)) === -1) linked.accountIds.push(String(t.accountId));
      linked.net += netPnl(t);
      linked.exit = Math.max(linked.exit, exitMs(t));
      continue;
    }
    const last = out[out.length - 1];
    if (last && last.symbol === t.symbol && last.side === t.side && last.accountIds.indexOf(String(t.accountId)) === -1
      && ms(t.date) - last.start <= SAME_DECISION_MS) {
      last.trades.push(t);
      last.accountIds.push(String(t.accountId));
      last.net += netPnl(t);
      last.exit = Math.max(last.exit, exitMs(t));
      continue;
    }
    out.push({ trades: [t], symbol: t.symbol, side: t.side, start: ms(t.date), exit: exitMs(t), net: netPnl(t),
      exposure: exposure(t), day: tradingDayKey(t.date), accountIds: [String(t.accountId)] });
    if (t.linkId) byLink.set(t.linkId, out[out.length - 1]);
  }
  return out;
}

// The member's normal: median decisions per trading day, median position, the half hours
// (New York time) that hold 85% of their trades, and how long winners and losers are held.
export function baselineOf(trades) {
  const decisions = decisionsOf(trades);
  const perDay = {};
  for (const d of decisions) perDay[d.day] = (perDay[d.day] || 0) + 1;
  const slots = {};
  for (const d of decisions) {
    const s = nySlot(d.trades[0]);
    slots[s] = (slots[s] || 0) + 1;
  }
  let usualSlots = null;
  if (decisions.length >= MIN_FOR_HOURS) {
    usualSlots = new Set();
    let covered = 0;
    for (const [s, n] of Object.entries(slots).sort((a, b) => b[1] - a[1])) {
      if (covered / decisions.length >= USUAL_SHARE) break;
      usualSlots.add(Number(s));
      covered += n;
    }
  }
  const held = trades.filter((t) => !t.timeUnknown && holdMs(t) !== null);
  const wins = held.filter((t) => netPnl(t) > 0);
  const losses = held.filter((t) => netPnl(t) < 0);
  const avg = (list) => (list.length ? list.reduce((s, t) => s + netPnl(t), 0) / list.length : 0);
  return {
    tradesPerDay: median(Object.values(perDay)),
    exposure: median(decisions.map((d) => d.exposure)),
    usualSlots,
    decisions,
    holds: wins.length >= MIN_FOR_HOLDS && losses.length >= MIN_FOR_HOLDS
      ? { win: median(wins.map(holdMs)), loss: median(losses.map(holdMs)), avgWin: avg(wins), avgLoss: avg(losses) }
      : null,
  };
}

// "9:30 AM – 11:30 AM, 1:30 PM – 3:00 PM" from the usual half hours, each unbroken run as one range.
export function usualHoursText(usualSlots) {
  if (!usualSlots || !usualSlots.size) return '';
  const ranges = [];
  for (const s of [...usualSlots].sort((a, b) => a - b)) {
    const last = ranges[ranges.length - 1];
    if (last && s === last[1] + 1) last[1] = s;
    else ranges.push([s, s]);
  }
  return ranges.map(([a, b]) => `${slotLabel(a)} – ${slotLabel(b + 1)}`).join(', ');
}

const contracts = (n) => tn(n === 1 ? '{n} contract' : '{n} contracts', { n });

// The trade ids that came from a trade idea, and the days the member wrote ideas (TradeIdeas.js).
function ideasOf(notes) {
  const ids = new Set();
  const days = new Set();
  for (const [key, n] of Object.entries(notes || {})) {
    if (!n || !(n.ideas || []).length) continue;
    days.add(n.date || key);
    for (const i of n.ideas) if (i.tradeId) ids.add(String(i.tradeId));
  }
  return { ids, days };
}

// What the member reported, per group: days they checked an emotion in the End-of-day
// reflection or tapped a reset button in Live session, with their P&L on those days.
function reportedOf(trades, notes) {
  const pnlByDay = {};
  for (const t of trades) pnlByDay[dayKey(t.date)] = (pnlByDay[dayKey(t.date)] || 0) + netPnl(t);
  const tradeDays = Object.keys(pnlByDay);
  const out = {};
  for (const g of GROUPS) {
    const days = new Set();
    let taps = 0;
    for (const [key, n] of Object.entries(notes || {})) {
      if (!n) continue;
      const day = n.date || key;
      if ((n.emotionsHit || []).some((e) => g.feelings.includes(e))) days.add(day);
      const tapped = ((n.sessionLog) || []).filter((e) => e.feeling && g.feelings.includes(e.feeling)).length;
      if (tapped) { taps += tapped; days.add(day); }
    }
    const withTrades = [...days].filter((d) => pnlByDay[d] !== undefined);
    const others = tradeDays.filter((d) => !days.has(d));
    const avg = (list) => (list.length ? list.reduce((s, d) => s + pnlByDay[d], 0) / list.length : 0);
    out[g.id] = { days: days.size, dayList: [...days], taps, tradeDays: withTrades.length, avg: avg(withTrades), otherAvg: avg(others), otherDays: others.length };
  }
  return out;
}

// Every flagged trade with why, a summary per pattern and per group, and what was reported.
// `baselineTrades` (defaults to `trades`) sets what's usual; `accounts` give daily loss limits;
// `notes` (day notes by date) give trade ideas and reported emotions.
export function detectBehavior(trades, { accounts = [], baselineTrades, notes } = {}) {
  const base = baselineOf(baselineTrades || trades);
  const decisions = baselineTrades ? decisionsOf(trades) : base.decisions;
  const overLimit = Math.max(4, Math.ceil(base.tradesPerDay * 2));
  const flags = new Map(); // trade id → [{ kind, text }]
  const flagTrade = (t, kind, text) => {
    const list = flags.get(t._id) || [];
    if (!list.some((f) => f.kind === kind)) list.push({ kind, text });
    flags.set(t._id, list);
  };
  const flag = (d, kind, text) => d.trades.forEach((t) => flagTrade(t, kind, text));
  const bigger = (d, prev) => base.exposure && d.exposure >= prev.exposure * 1.5 && d.exposure > base.exposure * 1.25;
  const typicalMove = median(decisions.map((d) => Math.abs(d.net)));

  // Per day, in order.
  const byDay = {};
  for (const d of decisions) (byDay[d.day] = byDay[d.day] || []).push(d);
  for (const list of Object.values(byDay)) {
    let losses = 0;
    let wins = 0;
    list.forEach((d, i) => {
      const prev = list[i - 1];
      if (prev && prev.net < 0) {
        const gap = (d.start - prev.exit) / 60000;
        if (gap >= 0 && gap <= REVENGE_MINUTES) {
          const mins = Math.round(gap);
          flag(d, 'revenge', tn('Entered {min} after a {amount} loss on {symbol}.', {
            min: gap < 1 ? tn('under a minute') : tn(mins === 1 ? '{n} minute' : '{n} minutes', { n: mins }), amount: formatMoney(prev.net), symbol: prev.symbol,
          }));
        }
        if (bigger(d, prev)) {
          flag(d, 'sizeUp', tn('{size} after a {amount} loss with {prev}.', {
            size: contracts(d.trades[0].size || 1), amount: formatMoney(prev.net), prev: contracts(prev.trades[0].size || 1),
          }));
        }
      }
      if (prev && wins >= 2 && bigger(d, prev)) {
        flag(d, 'winSizeUp', tn('{size} after {n} wins in a row with {prev}.', { size: contracts(d.trades[0].size || 1), n: wins, prev: contracts(prev.trades[0].size || 1) }));
      }
      if (losses >= STREAK) flag(d, 'streak', tn('Taken after {n} losses in a row.', { n: losses }));
      if (i + 1 > overLimit) flag(d, 'overtrading', tn('Trade {n} of the day. You usually take {usual}.', { n: i + 1, usual: Math.round(base.tradesPerDay) || 1 }));
      if (base.usualSlots && !base.usualSlots.has(nySlot(d.trades[0]))) {
        flag(d, 'offHours', tn('{time} New York time. You usually trade {hours}.', { time: slotLabel(nySlot(d.trades[0])), hours: usualHoursText(base.usualSlots) }));
      }
      losses = d.net < 0 ? losses + 1 : d.net > 0 ? 0 : losses;
      wins = d.net > 0 ? wins + 1 : d.net < 0 ? 0 : wins;
    });

    // Giving back the day's profit: the running P&L (by exit time) peaked, then lost at least half.
    const byExit = [...list].sort((a, b) => a.exit - b.exit);
    let run = 0;
    let peak = 0;
    let peakAt = null;
    for (const d of byExit) {
      run += d.net;
      if (run > peak) { peak = run; peakAt = d.exit; }
    }
    if (peakAt && peak >= Math.max(100, typicalMove * 2) && run <= peak / 2) {
      const after = byExit.filter((d) => d.exit > peakAt && d.net < 0);
      const lost = after.reduce((s, d) => s + d.net, 0);
      for (const d of after) {
        flag(d, 'giveBack', tn('You were up {peak} at {time} New York time. The losing trades after that took back {amount}.', { peak: formatMoney(peak), time: clock(peakAt), amount: formatMoney(lost) }));
      }
    }
  }

  // Cutting winners short: winners closed in under half the usual hold of a losing trade, when
  // winners are held much shorter than losers overall.
  if (base.holds && base.holds.win < base.holds.loss * 0.6) {
    for (const t of trades) {
      if (t.timeUnknown || netPnl(t) <= 0 || holdMs(t) === null || holdMs(t) >= base.holds.loss * 0.5) continue;
      flagTrade(t, 'cutWinners', tn('Closed for {amount} after {held}. Your losing trades usually stay open {loss}.', { amount: formatMoney(netPnl(t)), held: formatDuration(holdMs(t)), loss: formatDuration(base.holds.loss) }));
    }
  }

  // FOMO: on days with trade ideas, trades that weren't one of them.
  const ideas = ideasOf(notes);
  if (ideas.days.size) {
    for (const t of trades) {
      if (ideas.days.has(dayKey(t.date)) && !ideas.ids.has(String(t._id))) flagTrade(t, 'unplanned', tn('Not one of your trade ideas that day.'));
    }
  }

  // Daily loss limits, per account: the day's closed P&L before this trade's entry.
  const limits = new Map(accounts.filter((a) => a.dailyLossLimit > 0).map((a) => [String(a._id), a.dailyLossLimit]));
  if (limits.size) {
    const perAccountDay = {};
    for (const t of trades.filter((x) => !x.timeUnknown && limits.has(String(x.accountId)))) {
      const k = `${t.accountId}|${tradingDayKey(t.date)}`;
      (perAccountDay[k] = perAccountDay[k] || []).push(t);
    }
    for (const list of Object.values(perAccountDay)) {
      const limit = limits.get(String(list[0].accountId));
      for (const t of list) {
        const before = list.filter((o) => o !== t && exitMs(o) <= ms(t.date)).reduce((s, o) => s + netPnl(o), 0);
        if (before <= -limit) {
          flagTrade(t, 'pastLimit', tn('The account was already down {amount} that day (limit {limit}).', { amount: formatMoney(before), limit: formatMoney(-limit) }));
        }
      }
    }
  }

  const byId = new Map(trades.map((t) => [t._id, t]));
  const has = (t, kinds) => (flags.get(t._id) || []).some((f) => kinds.includes(f.kind));
  const costlyKinds = PATTERNS.filter((p) => !p.costless).map((p) => p.kind);
  const flaggedAll = [...flags.keys()].map((id) => byId.get(id)).filter(Boolean);
  // Quick winners aren't a cost, so they don't count toward "what your behavior cost you".
  const flagged = flaggedAll.filter((t) => has(t, costlyKinds));
  const clean = trades.filter((t) => !t.timeUnknown && !has(t, costlyKinds));
  const sum = (list) => list.reduce((s, t) => s + netPnl(t), 0);
  const stats = (list) => {
    const net = sum(list);
    return {
      trades: list, count: list.length, net, avg: list.length ? net / list.length : 0,
      winRate: list.length ? (list.filter((t) => netPnl(t) > 0).length / list.length) * 100 : 0,
      days: new Set(list.map((t) => tradingDayKey(t.date))).size,
    };
  };
  const patterns = PATTERNS.map((p) => ({ ...p, ...stats(flaggedAll.filter((t) => has(t, [p.kind]))) }));
  const reported = reportedOf(trades, notes);
  const groups = GROUPS.map((g) => {
    const list = patterns.filter((p) => p.group === g.id);
    const kinds = list.filter((p) => !p.costless).map((p) => p.kind);
    const seen = new Set(flaggedAll.filter((t) => has(t, list.map((p) => p.kind))).map((t) => dayKey(t.date)));
    // Reported days on which the trades show the same behavior.
    const overlap = reported[g.id].dayList.filter((d) => seen.has(d)).length;
    return { ...g, patterns: list, detected: stats(flagged.filter((t) => has(t, kinds))), reported: reported[g.id], overlap };
  });

  // The days with costly flagged trades, costliest first.
  const days = {};
  for (const t of flagged) {
    const k = tradingDayKey(t.date);
    (days[k] = days[k] || { day: k, trades: [], kinds: new Set() }).trades.push(t);
    flags.get(t._id).forEach((f) => costlyKinds.includes(f.kind) && days[k].kinds.add(f.kind));
  }
  const dayTotals = {};
  for (const t of trades) dayTotals[tradingDayKey(t.date)] = (dayTotals[tradingDayKey(t.date)] || 0) + netPnl(t);
  const worstDays = Object.values(days)
    .map((d) => ({ ...d, kinds: PATTERNS.map((p) => p.kind).filter((k) => d.kinds.has(k)), flaggedNet: sum(d.trades), dayNet: dayTotals[d.day] || 0 }))
    .sort((a, b) => a.flaggedNet - b.flaggedNet);

  return {
    flags, patterns, groups, worstDays,
    flagged: stats(flagged), clean: stats(clean),
    baseline: {
      tradesPerDay: base.tradesPerDay, usualHours: usualHoursText(base.usualSlots), overLimit, hasHours: !!base.usualSlots,
      holds: base.holds, hasIdeas: ideas.days.size > 0,
    },
    timedCount: trades.filter((t) => !t.timeUnknown).length,
  };
}

// The rule to suggest for a pattern, with the member's numbers filled in.
export function ruleFor(kind, baseline) {
  const p = PATTERNS.find((x) => x.kind === kind);
  return p ? tn(p.rule, { n: Math.max(2, Math.round(baseline.tradesPerDay * 1.5) || 3) }) : '';
}
